import React, { useState, useEffect } from 'react';
import { 
  Download, 
  RefreshCw, 
  Trash2, 
  Archive, 
  Calendar, 
  HardDrive, 
  AlertCircle,
  CheckCircle,
  Clock,
  Database
} from 'lucide-react';
import { backupAPI } from '../utils/api';
import toast from 'react-hot-toast';

function BackupManager() {
  const [backups, setBackups] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(new Set());

  // Component yüklendiğinde verileri al
  useEffect(() => {
    fetchBackups();
    fetchStatistics();
  }, []);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const response = await backupAPI.getList();
      
      if (response.data.success) {
        setBackups(response.data.data.backups || []);
      } else {
        console.error('Yedek listesi alınamadı:', response.data.message);
      }
    } catch (error) {
      console.error('API hatası:', error);
      toast.error('Yedek listesi alınırken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await backupAPI.getStatistics();
      
      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('İstatistik hatası:', error);
    }
  };

  const handleManualBackup = async () => {
    try {
      setCreating(true);
      toast.loading('Yedek oluşturuluyor...', { id: 'backup-creating' });
      
      const response = await backupAPI.createManual();
      
      if (response.data.success) {
        toast.success('Yedekleme başarıyla tamamlandı!', { id: 'backup-creating' });
        
        // Listeleri yenile
        await fetchBackups();
        await fetchStatistics();
      } else {
        toast.error(response.data.message || 'Yedekleme başarısız', { id: 'backup-creating' });
      }
    } catch (error) {
      console.error('Manuel yedekleme hatası:', error);
      toast.error('Yedekleme sırasında hata oluştu', { id: 'backup-creating' });
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (filename, fileType) => {
    try {
      toast.loading(`${filename} indiriliyor...`, { id: 'downloading' });
      
      const response = await backupAPI.downloadFile(filename);
      
      // Blob oluştur ve indir
      const blob = new Blob([response.data], { 
        type: fileType === 'zip' ? 'application/zip' : 'application/json' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Dosya başarıyla indirildi', { id: 'downloading' });
    } catch (error) {
      console.error('Dosya indirme hatası:', error);
      toast.error('Dosya indirme sırasında hata oluştu', { id: 'downloading' });
    }
  };

  const handleCleanup = async (daysToKeep = 30) => {
    if (!window.confirm(`${daysToKeep} günden eski yedekleri silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      toast.loading('Eski yedekler temizleniyor...', { id: 'cleanup' });
      
      const response = await backupAPI.cleanup(daysToKeep);
      
      if (response.data.success) {
        toast.success(
          `${response.data.data.deletedCount} yedek silindi, ${response.data.data.freedSpaceFormatted} yer açıldı`,
          { id: 'cleanup' }
        );
        
        // Listeleri yenile
        await fetchBackups();
        await fetchStatistics();
      } else {
        toast.error(response.data.message || 'Temizlik başarısız', { id: 'cleanup' });
      }
    } catch (error) {
      console.error('Temizlik hatası:', error);
      toast.error('Temizlik sırasında hata oluştu', { id: 'cleanup' });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    return type === 'zip' ? <Archive size={16} /> : <Database size={16} />;
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <HardDrive className="text-blue-600" />
            <span>Yedekleme Yönetimi</span>
          </h1>
          <p className="text-gray-600 mt-1">
            Veritabanı yedeklerini oluşturun, indirin ve yönetin
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={() => {
              fetchBackups();
              fetchStatistics();
            }}
            disabled={loading}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            <span>Yenile</span>
          </button>
          
          <button
            onClick={handleManualBackup}
            disabled={creating}
            className="btn-primary flex items-center space-x-2"
          >
            {creating ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Download size={16} />
            )}
            <span>{creating ? 'Oluşturuluyor...' : 'Manuel Yedek Al'}</span>
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Toplam Yedek</p>
                <p className="text-2xl font-bold text-blue-600">
                  {statistics.overview.totalBackups}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Toplam Dosya</p>
                <p className="text-2xl font-bold text-green-600">
                  {statistics.overview.totalFiles}
                </p>
              </div>
              <Archive className="h-8 w-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Toplam Boyut</p>
                <p className="text-2xl font-bold text-purple-600">
                  {statistics.overview.totalSizeFormatted}
                </p>
              </div>
              <HardDrive className="h-8 w-8 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Son Yedek</p>
                <p className="text-sm font-bold text-gray-900">
                  {statistics.overview.newestBackup || 'Yok'}
                </p>
              </div>
              <Clock className="h-8 w-8 text-gray-500" />
            </div>
          </div>
        </div>
      )}

      {/* Weekly Backup Status */}
      {statistics && statistics.last7Days && (
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <h3 className="text-lg font-semibold mb-4">Son 7 Günlük Yedekleme Durumu</h3>
          <div className="flex space-x-2">
            {statistics.last7Days.map((day, index) => (
              <div key={index} className="flex-1">
                <div className="text-center">
                  <div className={`h-8 w-full rounded ${
                    day.hasBackup ? 'bg-green-500' : 'bg-red-200'
                  } flex items-center justify-center`}>
                    {day.hasBackup ? (
                      <CheckCircle size={16} className="text-white" />
                    ) : (
                      <AlertCircle size={16} className="text-red-500" />
                    )}
                  </div>
                  <p className="text-xs mt-1 text-gray-600">
                    {new Date(day.date).toLocaleDateString('tr-TR', { 
                      day: '2-digit', 
                      month: '2-digit' 
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>7 gün önce</span>
            <span>Bugün</span>
          </div>
        </div>
      )}

      {/* Backup List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Mevcut Yedekler</h3>
            
            <button
              onClick={() => handleCleanup(30)}
              className="btn-danger-outline flex items-center space-x-2 text-sm"
            >
              <Trash2 size={14} />
              <span>30 Gün+ Temizle</span>
            </button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="animate-spin mr-2" />
              <span>Yedekler yükleniyor...</span>
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <HardDrive size={48} className="mx-auto mb-4 text-gray-300" />
              <p>Henüz yedek bulunmuyor</p>
              <p className="text-sm">Manuel yedekleme başlatabilirsiniz</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tarih
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dosyalar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Toplam Boyut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {backups.map((backup) => (
                  <tr key={backup.date} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar size={16} className="text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {backup.date}
                          </div>
                          <div className="text-sm text-gray-500">
                            {backup.files.length} dosya
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {backup.files.map((file) => (
                          <div key={file.filename} className="flex items-center text-sm">
                            {getFileIcon(file.type)}
                            <span className="ml-2 text-gray-900">{file.filename}</span>
                            <span className="ml-2 text-gray-500">({file.sizeFormatted})</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatFileSize(backup.totalSize)}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        {backup.files.map((file) => (
                          <button
                            key={file.filename}
                            onClick={() => handleDownload(file.filename, file.type)}
                            className="btn-secondary-sm flex items-center space-x-1"
                            title={`${file.filename} dosyasını indir`}
                          >
                            <Download size={14} />
                            <span>{file.type.toUpperCase()}</span>
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Auto Backup Info */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Clock className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
          <div>
            <h4 className="text-sm font-medium text-blue-900">
              Otomatik Yedekleme Bilgisi
            </h4>
            <p className="text-sm text-blue-700 mt-1">
              Sistem her gün saat 02:00'de otomatik yedek alır. 
              30 günden eski yedekler otomatik olarak temizlenir.
              Haftalık temizlik her Pazar 03:00'te çalışır.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BackupManager; 